import { area } from '@turf/turf'
import type { Feature, FeatureCollection } from 'geojson'

import { UniqueEntityID } from '@/domain/core'
import { UseCase } from '@/domain/core/UseCase'
import { Result } from '@/domain/protocols/Result'
import { canManageUserGeometryLayers, LayerStyle } from '@/domain/protocols/UserGeometry'

import type { IUserGeometryRepository } from '@/data/UserGeometryRepository/IUserGeometryRepository'
import {
  deserializeFlatGeobufBytes,
  serializeFeatureCollectionToFlatGeobuf
} from '@/data/UserGeometryRepository/userGeometryFlatGeobuf'

type GenericProperties = Record<string, unknown>

const DEFAULT_LABEL_MIN_ZOOM = 6

const clampOpacity = (value: number): number => Math.max(0, Math.min(1, value))

const normalizeBoolean = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null)

const normalizeOpacity = (value: unknown): number | null => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  return Math.max(0, Math.min(1, numeric))
}

// Whether the fill should render, inferred from the existing fill color + opacity
// when the edit doesn't state it explicitly (matches the mobile resolver).
const resolveExistingFillEnabled = (properties: GenericProperties): boolean => {
  const fillColor = typeof properties.fill === 'string' ? properties.fill.trim() : ''
  const fillOpacity = normalizeOpacity(properties['fill-opacity'])
  if (!fillColor && fillOpacity == null) {
    return true
  }
  return fillColor ? fillOpacity == null || fillOpacity > 0 : fillOpacity != null && fillOpacity > 0
}

const resolveExistingLabelMinZoom = (properties: GenericProperties): number | null => {
  const parsed = Number(properties.labelMinZoom)
  if (!Number.isFinite(parsed)) return null
  return Math.max(3, Math.min(14, parsed))
}

// Polygon label min-zoom from its area: log10(area m²) is mapped from [1e4, 1e12]
// (log10 in [4, 12]) to zoom [12, 4], rounded to the nearest half-step; tiny areas
// clamp to 1 m² (→ zoom 12) and an area failure falls back to 6.
const resolvePolygonLabelMinZoom = (feature: Feature): number => {
  try {
    const squareMeters = area(feature)
    const safeArea = Math.max(squareMeters, 1)
    const MIN_LOG = 4
    const MAX_LOG = 12
    const MIN_ZOOM = 4
    const MAX_ZOOM = 12
    const areaPosition = (Math.log10(safeArea) - MIN_LOG) / (MAX_LOG - MIN_LOG)
    const normalized = Math.max(0, Math.min(1, areaPosition))
    const rawZoom = MAX_ZOOM - normalized * (MAX_ZOOM - MIN_ZOOM)

    return Math.round(rawZoom * 2) / 2
  } catch {
    return DEFAULT_LABEL_MIN_ZOOM
  }
}

// Applies a per-feature LayerStyle edit, writing only the managed style properties
// that match the feature's geometry kind: points keep `marker-color` (stroke/fill
// dropped); lines/polygons keep `stroke` (polygons also `fill` + an area-derived
// label zoom). Opacities are clamped; `fill-opacity` is 0 when the fill is off.
// Flags not present in the edit fall back to the feature's existing values.
export const buildEditedFeature = (feature: Feature, style: LayerStyle): Feature => {
  const properties: GenericProperties = { ...((feature.properties as GenericProperties) ?? {}) }

  const geometryType = feature.geometry?.type
  const isPoint = geometryType === 'Point' || geometryType === 'MultiPoint'
  const isPolygon = geometryType === 'Polygon' || geometryType === 'MultiPolygon'

  const nextStrokeOpacity = clampOpacity(style.strokeOpacity)
  const nextFillOpacity = clampOpacity(style.fillOpacity)
  const nextFillEnabled =
    typeof style.fillEnabled === 'boolean' ? style.fillEnabled : resolveExistingFillEnabled(properties)
  const nextHidden = typeof style.hidden === 'boolean' ? style.hidden : (normalizeBoolean(properties.hidden) ?? false)
  // Labels only apply to points/polygons; lines never carry a label.
  const nextShowLabel =
    isPoint || isPolygon
      ? typeof style.showLabel === 'boolean'
        ? style.showLabel
        : (normalizeBoolean(properties.showLabel) ?? false)
      : false

  // An explicit zoom wins; otherwise keep the existing one, else derive it (polygon)
  // or fall back to the default.
  const explicitLabelMinZoom =
    style.labelMinZoom != null && Number.isFinite(style.labelMinZoom) ? style.labelMinZoom : null
  const nextLabelMinZoom =
    explicitLabelMinZoom ??
    resolveExistingLabelMinZoom(properties) ??
    (isPolygon ? resolvePolygonLabelMinZoom(feature) : DEFAULT_LABEL_MIN_ZOOM)

  // An explicit (non-empty) layer name wins; otherwise keep the existing one.
  if (typeof style.name === 'string' && style.name.trim() !== '') {
    properties.name = style.name.trim()
  }

  properties.hidden = nextHidden
  properties.showLabel = nextShowLabel
  properties.labelMinZoom = nextLabelMinZoom
  properties['line-style'] = style.lineStyle ?? 'solid'

  if (isPoint) {
    delete properties.stroke
    delete properties.fill
    properties['marker-color'] = style.color
  } else {
    delete properties['marker-color']
    properties.stroke = style.color
  }

  if (isPolygon) {
    properties.fill = style.color
  }

  properties['stroke-opacity'] = nextStrokeOpacity
  properties['fill-opacity'] = nextFillEnabled ? nextFillOpacity : 0

  return { ...feature, properties }
}

export interface UpdateGeometriesLayersRequest {
  geometryId: string
  // Per-feature styles aligned by index to the geometry's stored features.
  layers: LayerStyle[]
}

// Pluggable FlatGeobuf codec so the use case can be unit-tested without real bytes.
export interface FlatGeobufCodec {
  deserialize: (bytes: Uint8Array) => Promise<FeatureCollection>
  serialize: (collection: FeatureCollection) => Uint8Array
}

const DEFAULT_CODEC: FlatGeobufCodec = {
  deserialize: deserializeFlatGeobufBytes,
  serialize: serializeFeatureCollectionToFlatGeobuf
}

const blobToUint8Array = async (blob: Uint8Array | Blob): Promise<Uint8Array> => {
  if (blob instanceof Uint8Array) {
    return blob
  }
  return new Uint8Array(await blob.arrayBuffer())
}

// Rewrites the per-feature styles inside a saved geometry's `.fgb` (only allowed for
// files with ≤ 30 features), then re-persists the processed blob and marks the
// geometry unsynced via the repository.
export class UpdateGeometriesLayersUseCase implements UseCase<UpdateGeometriesLayersRequest, Promise<Result<void>>> {
  constructor(
    private readonly repository: IUserGeometryRepository,
    private readonly codec: FlatGeobufCodec = DEFAULT_CODEC
  ) {}

  async execute(request: UpdateGeometriesLayersRequest): Promise<Result<void>> {
    try {
      const id = new UniqueEntityID(request.geometryId)

      const geometryResult = await this.repository.getGeometry(id)
      if (geometryResult.isFailure) {
        return Result.fail(geometryResult.error)
      }

      const fileResult = await this.repository.getFile(id, 'processed')
      if (fileResult.isFailure) {
        return Result.fail(fileResult.error)
      }

      const file = fileResult.getValue()
      if (!file) {
        return Result.fail('PROCESSED_FILE_NOT_FOUND')
      }

      const bytes = await blobToUint8Array(file.blob)
      const collection = await this.codec.deserialize(bytes)
      const features = Array.isArray(collection.features) ? collection.features : []

      // Per-feature editing is gated to small files (≤ 30 features).
      if (!canManageUserGeometryLayers(features.length)) {
        return Result.fail('LAYER_EDIT_NOT_ALLOWED')
      }

      const editedFeatures = features.map((feature, index) => {
        const style = request.layers[index]
        return style ? buildEditedFeature(feature, style) : feature
      })

      const editedCollection: FeatureCollection = {
        ...collection,
        type: 'FeatureCollection',
        features: editedFeatures
      }

      const fgbBytes = this.codec.serialize(editedCollection)

      const putResult = await this.repository.putFile(id, 'processed', { blob: fgbBytes })
      if (putResult.isFailure) {
        return Result.fail(putResult.error)
      }

      // Marks unsynced, bumps updatedAt and emits UserGeometryChanged.
      return await this.repository.updateGeometry(geometryResult.getValue())
    } catch (error) {
      return Result.fail(error)
    }
  }
}
